import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import Row from '@/components/utilities/row/row';
import { useI18n } from '@/libraries/contexts/i18n-context';
import styles from './room-description.module.scss';

export interface RoomDescriptionProps extends ModuleWrapperProps {
  /** The text to show as a headline */
  headline?: string;
  /** The text to show as a description */
  text: string;
}

const RoomDescription = ({ headline, text, ...rest }: RoomDescriptionProps) => {
  const { t } = useI18n();
  const textRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (!textRef.current) return;

    const isOverflowing =
      textRef.current.scrollHeight > textRef.current.clientHeight;

    setHasOverflow(isOverflowing);
  }, [textRef]);

  const handleToggleExpansion = () => {
    setIsExpanded(previous => !previous);
  };

  return (
    <ModuleWrapper
      {...rest}
      className={classNames(styles.component, {
        [styles.hasOverflow]: hasOverflow,
        [styles.isExpanded]: isExpanded,
      })}
    >
      <Container>
        <Row>
          <Column
            className={styles.contentColumn}
            offset={{ desktop: 3, laptop: 2 }}
            width={{ desktop: 6, laptop: 8 }}
          >
            {headline && (
              <Typography
                className={styles.headline}
                tag="h2"
                tagStyle="displayMedium"
              >
                {headline}
              </Typography>
            )}

            {text && (
              <div ref={textRef} className={styles.text}>
                <RichText text={text} />
              </div>
            )}

            {hasOverflow && (
              <button onClick={handleToggleExpansion}>
                <Typography
                  className={styles.viewMoreText}
                  tagStyle="bodyMedium"
                >
                  {isExpanded
                    ? `- ${t('items.common.words.showLess')}`
                    : `+ ${t('items.common.words.showMore')}`}
                </Typography>
              </button>
            )}
          </Column>
        </Row>
      </Container>
    </ModuleWrapper>
  );
};

RoomDescription.displayName = 'RoomDescription';

export default RoomDescription;
